
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { analyzePrompt } from './services/geminiService';
import { AnalysisResult, LoadingState, UserProfile, HistoryItem, Country, SUPPORTED_COUNTRIES, UserDetails, UserDetailsInput } from './types';
import { logout, subscribeToAuthChanges } from './services/authService';
import { subscribeToHistory, saveToHistory, clearHistory } from './services/historyService';
import { getUserDetails, saveUserDetails } from './services/userDetailsService';
import { shouldRequireUserDetails } from './services/userDetailsValidation';
import { checkRateLimit, recordUsage } from './services/rateLimitService';
import { safeGetItem, safeSetItem, createStorageAccessor } from './services/storageService';
import { initVersionTracking, APP_VERSION, getBuildInfo } from './services/versionService';
import { isAdmin } from './services/adminService';
import AnalysisView from './components/AnalysisView';
import LoginView from './components/LoginView';
import HistorySidebar from './components/HistorySidebar';
import CountrySelector from './components/CountrySelector';
import DoctIABanner from './components/DoctIABanner';
import AdminDashboard from './components/AdminDashboard';
import TermsConditions from './components/TermsConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import UserDetailsModal from './components/UserDetailsModal';
import { Send, AlertTriangle, LogOut, History as HistoryIcon, ChevronDown, Globe, Instagram, Clock, Plus, Shield } from 'lucide-react';

// Initialize version tracking and handle cache-busting
const { isNewVersion, needsMigration } = initVersionTracking();
if (isNewVersion) {
  console.log(`App updated to version ${APP_VERSION}`);
}
if (needsMigration) {
  console.log('Storage migration needed');
}

// Log build info in development
if (import.meta.env.DEV) {
  console.log('Build info:', getBuildInfo());
}

// Type guard for country code validation
const isValidCountryCode = (code: unknown): code is string => {
  return typeof code === 'string' && SUPPORTED_COUNTRIES.some(c => c.code === code);
};

// Create safe country storage accessor
const createCountryStorage = (userId: string) => createStorageAccessor<string>(
  `legal_prompt_country_${userId}`,
  SUPPORTED_COUNTRIES[0].code,
  'cookie',
  isValidCountryCode
);


function App() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [promptInput, setPromptInput] = useState('');
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  // History State
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [isUserDetailsLoaded, setIsUserDetailsLoaded] = useState(false);
  const [isUserDetailsModalOpen, setIsUserDetailsModalOpen] = useState(false);
  const [pendingAnalyzeAfterDetails, setPendingAnalyzeAfterDetails] = useState(false);

  // Country State
  const [selectedCountry, setSelectedCountry] = useState<Country>(SUPPORTED_COUNTRIES[0]); // Default Argentina
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [hasCountryPreference, setHasCountryPreference] = useState(false);

  // Admin State
  const [isAdminDashboardOpen, setIsAdminDashboardOpen] = useState(false);

  // Navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Monitor Auth State
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((currentUser) => {
      setUser(currentUser);
      setIsAuthChecking(false);
    });
    return () => unsubscribe();
  }, []);

  // Load history when user logs in
  useEffect(() => {
    if (user) {
        let isActive = true;
        setIsHistoryLoaded(false);
        setIsUserDetailsLoaded(false);

        // Subscribe to real-time updates
        const unsubscribeHistory = subscribeToHistory(user.id, (items) => {
            setHistory(items);
            setIsHistoryLoaded(true);
        });

        getUserDetails(user.id)
          .then((details) => {
            if (isActive) setUserDetails(details);
          })
          .catch((error) => {
            console.error('Failed to load user details', error);
            if (isActive) setUserDetails(null);
          })
          .finally(() => {
            if (isActive) setIsUserDetailsLoaded(true);
          });
        
        // Load preference using safe storage
        const countryStorage = createCountryStorage(user.id);
        const savedCountryCode = countryStorage.get();
        
        if (savedCountryCode && isValidCountryCode(savedCountryCode)) {
            const country = SUPPORTED_COUNTRIES.find(c => c.code === savedCountryCode);
            if (country) {
                setSelectedCountry(country);
                setHasCountryPreference(true);
            } else {
                setIsCountryModalOpen(true);
            }
        } else {
            // Check legacy localStorage and migrate
            try {
                const legacy = localStorage.getItem(`legal_prompt_country_${user.id}`);
                if (legacy && isValidCountryCode(legacy)) {
                   const country = SUPPORTED_COUNTRIES.find(c => c.code === legacy);
                   if (country) {
                       setSelectedCountry(country);
                       setHasCountryPreference(true);
                       // Migrate to new storage format
                       countryStorage.set(legacy, { expires: 365 });
                       localStorage.removeItem(`legal_prompt_country_${user.id}`);
                   } else {
                       setIsCountryModalOpen(true);
                   }
                } else {
                   setIsCountryModalOpen(true);
                }
            } catch (e) {
                console.warn('Error migrating legacy country preference:', e);
                setIsCountryModalOpen(true);
            }
        }

        return () => {
          isActive = false;
          unsubscribeHistory();
        };
    } else {
        setHistory([]);
        setHasCountryPreference(false);
        setUserDetails(null);
        setIsHistoryLoaded(false);
        setIsUserDetailsLoaded(false);
        setIsUserDetailsModalOpen(false);
        setPendingAnalyzeAfterDetails(false);
    }
  }, [user]);

  const handleLoginSuccess = (userProfile: UserProfile) => {
    setUser(userProfile);
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    resetApp();
    setIsSidebarOpen(false);
  };

  const handleCountrySelect = (country: Country) => {
      setSelectedCountry(country);
      setHasCountryPreference(true);
      if (user) {
        const countryStorage = createCountryStorage(user.id);
        countryStorage.set(country.code, { expires: 365 });
      }
      setIsCountryModalOpen(false);
      // Reset result if switching countries
      if (result) {
          resetApp();
      }
  };

  const runAnalysis = async () => {
    // Rate Limit Check
    const limitStatus = checkRateLimit();
    if (!limitStatus.allowed) {
      setErrorMsg(`Has alcanzado el límite de uso. Por favor esperá ${limitStatus.waitTimeMinutes} minuto(s) para realizar un nuevo análisis.`);
      setLoadingState(LoadingState.ERROR);
      return;
    }
    
    setLoadingState(LoadingState.ANALYZING);
    setErrorMsg(null);
    
    try {
      // Record usage before call (to count attempts and prevent spam)
      recordUsage();

      const data = await analyzePrompt(promptInput, selectedCountry);
      setResult(data);
      setLoadingState(LoadingState.COMPLETE);
      
      // Save to history automatically
      if (user) {
          // No need to setHistory manually, the subscription will pick it up
          await saveToHistory(user.id, promptInput, data);
      }

    } catch (err) {
      console.error(err);
      setErrorMsg("Ocurrió un error al conectar con la IA. Por favor, verificá tu conexión o intentá nuevamente.");
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleAnalyze = async () => {
    if (!promptInput.trim()) return;

    if (user && !isHistoryLoaded) {
      setErrorMsg('Estamos cargando tu historial. Intentá nuevamente en unos segundos.');
      return;
    }

    if (user && history.length >= 2 && !isUserDetailsLoaded) {
      setErrorMsg('Estamos cargando tus datos. Intentá nuevamente en unos segundos.');
      return;
    }

    if (user && shouldRequireUserDetails(history.length, userDetails)) {
      setPendingAnalyzeAfterDetails(true);
      setIsUserDetailsModalOpen(true);
      return;
    }

    await runAnalysis();
  };

  const handleSaveUserDetails = async (details: UserDetailsInput) => {
    if (!user) return;

    const saved = await saveUserDetails(user.id, details);
    setUserDetails(saved);
    setIsUserDetailsModalOpen(false);

    if (pendingAnalyzeAfterDetails) {
      setPendingAnalyzeAfterDetails(false);
      await runAnalysis();
    }
  };

  const resetApp = () => {
    setResult(null);
    setLoadingState(LoadingState.IDLE);
    setPromptInput('');
    setErrorMsg(null);
  };

  const handleSelectHistory = (item: HistoryItem) => {
      setPromptInput(item.originalPrompt);
      setResult(item.analysisResult);
      setLoadingState(LoadingState.COMPLETE);
      setIsSidebarOpen(false);
  };

  const handleClearHistory = async () => {
      if (user && confirm('¿Estás seguro de que querés borrar todo el historial?')) {
          try {
              await clearHistory(user.id);
              // No need to setHistory([]), subscription handles it
          } catch (error) {
              console.error("Failed to clear history", error);
              alert("No se pudo borrar el historial. Intente nuevamente.");
          }
      }
  };

  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-gray-100 selection:bg-cyan-500/30 font-sans overflow-x-hidden flex flex-col">
      
      {/* Background Ambience */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-900/20 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-900/10 rounded-full blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-blue-900/20 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-150 contrast-150"></div>
      </div>

      {/* Sidebar Component */}
      <HistorySidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
        history={history}
        onSelect={handleSelectHistory}
        onClear={handleClearHistory}
      />

      {/* Country Selector Modal */}
      <CountrySelector 
        isOpen={isCountryModalOpen}
        onSelect={handleCountrySelect}
        selectedCountry={selectedCountry}
        isDismissible={hasCountryPreference}
        onClose={() => setIsCountryModalOpen(false)}
      />

      {/* Admin Dashboard Modal (only accessible by admin) */}
      {isAdmin(user) && (
        <AdminDashboard 
          isOpen={isAdminDashboardOpen}
          onClose={() => setIsAdminDashboardOpen(false)}
        />
      )}

      <UserDetailsModal
        isOpen={isUserDetailsModalOpen}
        initialDetails={userDetails}
        onSave={handleSaveUserDetails}
      />

      {/* Header */}
      <header className="relative z-10 w-full border-b border-zinc-900 bg-black/50 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          <div className="flex items-center gap-3">
            <button 
                onClick={resetApp}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.5)] flex-shrink-0 overflow-hidden">
                    <img src="/lps-logo-blue.png" alt="Legal Prompt Studio" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col items-start">
                    <h1 className="font-bold text-xl tracking-tight text-white leading-none mb-1">LegalPromptStudio</h1>
                    
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {/* Branding Links */}
                        <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 font-medium">
                            <span>by</span>
                            <a 
                                href="https://inteligencialegal.com.ar" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-cyan-500 hover:text-cyan-400 font-bold transition-colors"
                                onClick={(e) => e.stopPropagation()}
                            >
                                InteligenciaLegal
                            </a>
                            <div className="flex items-center gap-1 border-l border-zinc-800 pl-1.5 ml-0.5">
                                <a 
                                    href="https://inteligencialegal.com.ar" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:text-white transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Globe className="w-3 h-3" />
                                </a>
                                <a 
                                    href="https://instagram.com/inteligencialegal.ok" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:text-pink-500 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Instagram className="w-3 h-3" />
                                </a>
                            </div>
                        </div>

                        {/* Country Trigger */}
                        {user && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); setIsCountryModalOpen(true); }}
                                className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 font-bold hover:text-white transition-colors border-l border-zinc-800 pl-2 ml-1"
                            >
                                <img 
                                    src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`} 
                                    alt={selectedCountry.code}
                                    className="w-4 h-3 object-cover rounded-[2px]"
                                />
                                <span className="hidden sm:inline">{selectedCountry.name}</span>
                                <ChevronDown className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
            </button>
          </div>
          
          <div className="flex items-center gap-4 sm:gap-6">
            
            {/* Status Indicator: Only show if error */}
            {loadingState === LoadingState.ERROR && (
                <div className="hidden lg:flex items-center gap-2 text-sm text-red-400 bg-red-950/20 px-3 py-1 rounded-full border border-red-900/50">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                    IA fuera de servicio
                </div>
            )}
            
            {user && (
              <div className="flex items-center gap-4 pl-4 sm:pl-6 border-l border-zinc-800">
                
                {/* Admin Dashboard Button (only visible to admin) */}
                {isAdmin(user) && (
                    <button 
                        onClick={() => setIsAdminDashboardOpen(true)}
                        className="p-2 hover:bg-zinc-800 rounded-lg text-purple-400 hover:text-purple-300 transition-all"
                        title="Admin Dashboard"
                    >
                        <Shield className="w-5 h-5" />
                    </button>
                )}

                {/* New Analysis Button */}
                <button 
                    onClick={resetApp}
                    className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-cyan-400 transition-all"
                    title="Nuevo Análisis"
                >
                    <Plus className="w-5 h-5" />
                </button>

                {/* History Toggle Button */}
                <button 
                    onClick={() => setIsSidebarOpen(true)}
                    className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-cyan-400 transition-all relative group"
                    title="Historial de análisis"
                >
                    <HistoryIcon className="w-5 h-5" />
                    {history.length > 0 && (
                        <span className="absolute top-1.5 right-2 w-2 h-2 bg-cyan-500 rounded-full"></span>
                    )}
                </button>

                {/* Profile Section */}
                <div className="flex items-center gap-3">
                    {user.photoURL ? (
                        <img src={user.photoURL} alt={user.name} className="w-8 h-8 rounded-full border border-zinc-700 object-cover" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-cyan-900 flex items-center justify-center text-xs font-bold text-cyan-200 border border-zinc-700">
                            {user.name.charAt(0)}
                        </div>
                    )}
                    <button 
                        onClick={handleLogout}
                        className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-red-400 transition-colors"
                        title="Cerrar Sesión"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 w-full max-w-5xl mx-auto px-6 py-12 flex-grow flex flex-col">
        
        <Routes>
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={
            !user ? (
              <LoginView onLoginSuccess={handleLoginSuccess} />
            ) : result ? (
              <AnalysisView result={result} prompt={promptInput} onReset={resetApp} />
            ) : (
            <div className="flex-grow flex flex-col items-center justify-center max-w-3xl mx-auto w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-widest mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        100% Gratuito
                    </div>
                    {/* Added pb-2 to prevent text clipping of descenders */}
                    <h2 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 mb-4 tracking-tight leading-tight pb-2">
                        Optimizador de Prompts Legales
                    </h2>
                    <p className="text-lg text-gray-400 max-w-xl mx-auto leading-relaxed">
                        Analizá y perfeccioná tus prompts jurídicos según las <a href="https://www.instagram.com/p/DI02BbvRSO1/?img_index=1" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white underline decoration-zinc-600 hover:decoration-cyan-500 underline-offset-4 transition-all duration-300">mejores prácticas</a> y leyes de <span className="text-cyan-400 font-semibold">{selectedCountry.name}</span>.
                    </p>
                </div>

                <div className="w-full relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-40 blur transition duration-500"></div>
                    <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                        
                        <textarea
                            value={promptInput}
                            onChange={(e) => setPromptInput(e.target.value)}
                            placeholder="Pegá acá tu prompt legal (Ej: 'Actuá como un abogado y redactá una carta documento por falta de pago...')"
                            className="w-full h-48 bg-transparent text-gray-200 p-6 resize-none focus:outline-none placeholder-zinc-600 text-lg leading-relaxed font-light"
                        />
                        
                        <div className="bg-black/20 p-4 flex items-center justify-between border-t border-zinc-800/50 backdrop-blur-sm">
                            <div className="text-xs text-zinc-500 font-medium px-2">
                                {promptInput.length} caracteres
                            </div>
                            <button
                                onClick={handleAnalyze}
                                disabled={loadingState === LoadingState.ANALYZING || !promptInput.trim()}
                                className="bg-white hover:bg-gray-200 text-black font-bold py-3 px-8 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] active:scale-95"
                            >
                                {loadingState === LoadingState.ANALYZING ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                        Analizando...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Analizar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {errorMsg && (
                    <div className="mt-6 w-full p-4 bg-red-950/30 border border-red-900/50 rounded-xl flex items-start gap-3 text-red-200 max-w-2xl animate-in slide-in-from-top-2 mx-auto">
                        {errorMsg.includes('límite') ? <Clock className="w-5 h-5 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 flex-shrink-0" />}
                        <p className="text-sm font-medium">{errorMsg}</p>
                    </div>
                )}
                
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 w-full text-center">
                    {[
                        { title: 'Análisis de Rol', desc: 'Verificación de persona jurídica' },
                        { title: 'Contexto Legal', desc: `Leyes vigentes en ${selectedCountry.name}` },
                        { title: 'Formato de Salida', desc: 'Estructura profesional' }
                    ].map((feature, i) => (
                        <div key={i} className="p-4 rounded-2xl bg-zinc-900/30 border border-zinc-800/50 hover:bg-zinc-800/50 transition-colors">
                            <h3 className="text-gray-200 font-bold mb-1">{feature.title}</h3>
                            <p className="text-zinc-500 text-sm">{feature.desc}</p>
                        </div>
                    ))}
                </div>

                <DoctIABanner />

            </div>
            )
          } />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="w-full border-t border-zinc-900 py-8 z-10 bg-black/50">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-zinc-500">
                <span>Desarrollado por</span>
                <a 
                    href="https://inteligencialegal.com.ar" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-500 hover:text-white font-bold transition-colors"
                >
                    InteligenciaLegal
                </a>
            </div>
            
            {/* Legal Links */}
            <div className="flex items-center gap-4 text-xs text-zinc-600">
                <Link 
                    to="/terms"
                    className="hover:text-zinc-400 transition-colors"
                >
                    Términos y Condiciones
                </Link>
                <span className="text-zinc-800">|</span>
                <Link 
                    to="/privacy"
                    className="hover:text-zinc-400 transition-colors"
                >
                    Política de Privacidad
                </Link>
            </div>
            
            <div className="flex items-center gap-6">
                <a href="https://inteligencialegal.com.ar" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors" title="Sitio Web">
                    <Globe className="w-5 h-5" />
                </a>
                <a href="https://instagram.com/inteligencialegal.ok" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-pink-500 transition-colors" title="Instagram">
                    <Instagram className="w-5 h-5" />
                </a>
            </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
